import { useState, useEffect } from "react";
import { authFetch } from "../api";

const TODAY = new Date().toLocaleDateString("en-CA");
const TZ_OFFSET = -new Date().getTimezoneOffset();

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toLocaleDateString("en-CA");
};

// Enumerate every date in [from, to] inclusive
function dateRange(from, to) {
  const dates = [];
  const cur = new Date(from);
  const end = new Date(to);
  while (cur <= end) {
    dates.push(cur.toLocaleDateString("en-CA"));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

const SERVICES = [
  { key: "water",           label: "Water" },
  { key: "ors",             label: "ORS" },
  { key: "mobile_charging", label: "Charging" },
  { key: "rest_area",       label: "Rest Area" },
  { key: "training",        label: "Training" },
  { key: "first_aid",       label: "First Aid" },
  { key: "health_camp",     label: "Health Camp" },
];

const AGE_LABELS = {
  under_18: "<18", "18_30": "18-30", "31_45": "31-45", "46_60": "46-60", "60_plus": "60+",
};

export default function MISReport({ centers: allCenters, apiBase, onClose }) {
  const centers = allCenters.filter(c => c.is_open !== false);
  const [fromDate, setFromDate]       = useState(TODAY);
  const [toDate, setToDate]           = useState(TODAY);
  const [footfallData, setFootfall]       = useState({});
  const [exitData, setExit]               = useState({});
  const [consumablesData, setConsumables] = useState({});
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [view, setView]                   = useState("daily"); // "daily" | "summary"

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(
          `${apiBase}/mis/summary/all?from_date=${fromDate}&to_date=${toDate}&tz_offset_minutes=${TZ_OFFSET}`,
          { signal }
        );
        if (res.ok) {
          const { footfall, exit, daily_logs } = await res.json();
          setFootfall(footfall   || {});
          setExit(exit           || {});
          setConsumables(daily_logs || {});
        } else {
          setError(`Failed to load report (${res.status}). Please try again.`);
        }
      } catch (e) {
        if (e.name !== "AbortError") setError("Could not reach the server. Please check your connection and try again.");
      }
      if (!signal.aborted) setLoading(false);
    };

    fetchAll();
    return () => controller.abort();
  }, [fromDate, toDate]);

  // ── Build rows ─────────────────────────────────────────────────────────────

  const dates = dateRange(fromDate, toDate);

  // Only include open centers that have at least some data in the selected range
  const activeCenterIds = new Set([
    ...Object.keys(footfallData),
    ...Object.keys(exitData),
    ...Object.keys(consumablesData),
  ]);
  const activeCenters = centers.filter(c => activeCenterIds.has(c.id));

  const CONSUMABLE_FIELDS = [
    { key: "water_cans",           label: "Water Cans (20L)" },
    { key: "ors_packets",          label: "ORS Packets" },
    { key: "care_kits",            label: "Care Kits" },
    { key: "trainings",            label: "Training Sessions" },
    { key: "members_trained_male", label: "Trained (M)" },
    { key: "members_trained_female", label: "Trained (F)" },
  ];

  // Daily rows: one per center per date (only centers with data)
  const dailyRows = [];
  for (const date of dates) {
    for (const center of activeCenters) {
      const ff = footfallData[center.id]   || {};
      const ex = exitData[center.id]       || {};
      const cl = consumablesData[center.id] || {};
      const dayConsumables = (cl.by_date || {})[date] || {};
      dailyRows.push({
        date,
        center_id:   center.id,
        center_name: center.name,
        address:     center.address,
        footfall:    (ff.by_date || {})[date] || 0,
        exits:       (ex.by_date || {})[date] || 0,
        ...Object.fromEntries(CONSUMABLE_FIELDS.map(f => [f.key, dayConsumables[f.key] || 0])),
      });
    }
  }

  // Summary rows: one per center with data, aggregated for range
  const summaryRows = activeCenters.map(center => {
    const ff = footfallData[center.id]   || {};
    const ex = exitData[center.id]       || {};
    const cl = consumablesData[center.id] || {};
    const byGender  = ff.by_gender    || {};
    const byAge     = ff.by_age_group || {};
    const byService = ex.by_service   || {};
    const totals    = cl.total        || {};
    return {
      center_name: center.name,
      address:     center.address,
      footfall:    ff.total || 0,
      male:        byGender.male   || 0,
      female:      byGender.female || 0,
      other:       byGender.other  || 0,
      age_u18:     byAge.under_18  || 0,
      age_1830:    byAge["18_30"]  || 0,
      age_3145:    byAge["31_45"]  || 0,
      age_4660:    byAge["46_60"]  || 0,
      age_60plus:  byAge["60_plus"]|| 0,
      exits:       ex.total || 0,
      ...Object.fromEntries(SERVICES.map(s => [s.key, byService[s.key] || 0])),
      ...Object.fromEntries(CONSUMABLE_FIELDS.map(f => [f.key, totals[f.key] || 0])),
    };
  });

  // ── CSV export ─────────────────────────────────────────────────────────────

  const downloadCSV = () => {
    let csv;
    if (view === "daily") {
      const header = [
        "Date", "Center", "Address", "Footfall (Entries)", "Exits",
        ...CONSUMABLE_FIELDS.map(f => f.label),
      ].join(",");
      const rows = dailyRows
        .filter(r => r.footfall > 0 || r.exits > 0 || CONSUMABLE_FIELDS.some(f => r[f.key] > 0))
        .map(r => [
          r.date, `"${r.center_name}"`, `"${r.address}"`, r.footfall, r.exits,
          ...CONSUMABLE_FIELDS.map(f => r[f.key]),
        ].join(","));
      csv = [header, ...rows].join("\n");
    } else {
      const header = [
        "Center", "Address",
        "Total Footfall", "Male", "Female", "Other",
        "<18", "18-30", "31-45", "46-60", "60+",
        "Total Exits", ...SERVICES.map(s => s.label),
        ...CONSUMABLE_FIELDS.map(f => f.label),
      ].join(",");
      const rows = summaryRows
        .filter(r => r.footfall > 0 || r.exits > 0 || CONSUMABLE_FIELDS.some(f => r[f.key] > 0))
        .map(r => [
          `"${r.center_name}"`, `"${r.address}"`,
          r.footfall, r.male, r.female, r.other,
          r.age_u18, r.age_1830, r.age_3145, r.age_4660, r.age_60plus,
          r.exits, ...SERVICES.map(s => r[s.key]),
          ...CONSUMABLE_FIELDS.map(f => r[f.key]),
        ].join(","));
      csv = [header, ...rows].join("\n");
    }

    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `mis-report-${view}-${fromDate}-to-${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hasData = activeCenters.length > 0;

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>MIS Report</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
              {fromDate === toDate ? fromDate : `${fromDate} → ${toDate}`}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <label style={styles.dateLabel}>From</label>
            <input type="date" value={fromDate} max={toDate}
              onChange={e => setFromDate(e.target.value)} style={styles.datePicker} />
            <label style={styles.dateLabel}>To</label>
            <input type="date" value={toDate} min={fromDate} max={TODAY}
              onChange={e => setToDate(e.target.value)} style={styles.datePicker} />
            <button onClick={onClose} style={styles.closeBtn}>✕</button>
          </div>
        </div>

        <div style={styles.body}>

          {/* Controls row */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
            {/* Quick ranges */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[
                { label: "Today",   from: TODAY,       to: TODAY },
                { label: "7 days",  from: daysAgo(6),  to: TODAY },
                { label: "30 days", from: daysAgo(29), to: TODAY },
              ].map(r => (
                <button
                  key={r.label}
                  onClick={() => { setFromDate(r.from); setToDate(r.to); }}
                  style={styles.rangeBtn(fromDate === r.from && toDate === r.to)}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* View toggle + download */}
            <div style={{ display: "flex", gap: 8 }}>
              <div style={styles.toggleRow}>
                <button
                  onClick={() => setView("daily")}
                  style={styles.toggleBtn(view === "daily")}
                >
                  Daily
                </button>
                <button
                  onClick={() => setView("summary")}
                  style={styles.toggleBtn(view === "summary")}
                >
                  Summary
                </button>
              </div>
              <button
                onClick={downloadCSV}
                disabled={!hasData || loading}
                style={styles.downloadBtn(!hasData || loading)}
              >
                ↓ Download CSV
              </button>
            </div>
          </div>

          {loading && <div style={styles.empty}>Loading…</div>}
          {!loading && error && <div style={styles.error}>{error}</div>}
          {!loading && !error && !hasData && <div style={styles.empty}>No data for this date range.</div>}

          {/* ── Daily table ── */}
          {!loading && hasData && view === "daily" && (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th} colSpan={3}></th>
                    <th style={{ ...styles.th, background: "#e5f5ff", textAlign: "center" }} colSpan={2}>People</th>
                    <th style={{ ...styles.th, background: "#f0fdf4", textAlign: "center", borderLeft: "2px solid #ddd" }} colSpan={CONSUMABLE_FIELDS.length}>Consumables</th>
                  </tr>
                  <tr>
                    {["Date", "Center", "Address"].map(h => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                    <th style={{ ...styles.th, background: "#e5f5ff", textAlign: "center" }}>Footfall</th>
                    <th style={{ ...styles.th, background: "#e5f5ff", textAlign: "center" }}>Exits</th>
                    {CONSUMABLE_FIELDS.map((f, i) => (
                      <th key={f.key} style={{ ...styles.th, background: "#f0fdf4", textAlign: "center", borderLeft: i === 0 ? "2px solid #ddd" : undefined }}>{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.map((row, i) => {
                    const isEmpty = row.footfall === 0 && row.exits === 0 && CONSUMABLE_FIELDS.every(f => !row[f.key]);
                    return (
                      <tr
                        key={`${row.date}-${row.center_id}`}
                        style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", opacity: isEmpty ? 0.35 : 1 }}
                      >
                        <td style={styles.td}>{row.date}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>{row.center_name}</td>
                        <td style={{ ...styles.td, color: "#888", fontSize: 12 }}>{row.address}</td>
                        <td style={{ ...styles.td, textAlign: "center", color: "#0093dd", fontWeight: 700 }}>{row.footfall || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center", color: "#d94f2b", fontWeight: 700 }}>{row.exits || "—"}</td>
                        {CONSUMABLE_FIELDS.map((f, idx) => (
                          <td key={f.key} style={{ ...styles.td, textAlign: "center", color: "#2d7a4f", borderLeft: idx === 0 ? "2px solid #ddd" : undefined }}>{row[f.key] || "—"}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── Summary table ── */}
          {!loading && hasData && view === "summary" && (
            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th} rowSpan={2}>Center</th>
                    <th style={{ ...styles.th, borderRight: "2px solid #ddd" }} rowSpan={2}>Address</th>
                    <th style={{ ...styles.th, background: "#e5f5ff", borderRight: "2px solid #ddd" }} colSpan={8}>Footfall</th>
                    <th style={{ ...styles.th, background: "#fff0ed", borderRight: "2px solid #ddd" }} colSpan={8}>Exits</th>
                    <th style={{ ...styles.th, background: "#f0fdf4" }} colSpan={CONSUMABLE_FIELDS.length}>Consumables</th>
                  </tr>
                  <tr>
                    {/* Footfall sub-headers */}
                    {["Total", "Male", "Female", "Other", "<18", "18-30", "31-45", "46-60+"].map(h => (
                      <th key={h} style={{ ...styles.th, background: "#e5f5ff", fontSize: 11, borderRight: h === "46-60+" ? "2px solid #ddd" : undefined }}>{h}</th>
                    ))}
                    {/* Exit sub-headers */}
                    {["Total", ...SERVICES.map(s => s.label)].map((h, i) => (
                      <th key={h} style={{ ...styles.th, background: "#fff0ed", fontSize: 11, borderRight: i === SERVICES.length ? "2px solid #ddd" : undefined }}>{h}</th>
                    ))}
                    {/* Consumables sub-headers */}
                    {CONSUMABLE_FIELDS.map(f => (
                      <th key={f.key} style={{ ...styles.th, background: "#f0fdf4", fontSize: 11 }}>{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.map((row, i) => {
                    const isEmpty = row.footfall === 0 && row.exits === 0 && CONSUMABLE_FIELDS.every(f => !row[f.key]);
                    return (
                      <tr
                        key={row.center_name}
                        style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", opacity: isEmpty ? 0.35 : 1 }}
                      >
                        <td style={{ ...styles.td, fontWeight: 600, whiteSpace: "nowrap" }}>{row.center_name}</td>
                        <td style={{ ...styles.td, color: "#888", fontSize: 12, borderRight: "2px solid #ddd" }}>{row.address}</td>
                        {/* Footfall */}
                        <td style={{ ...styles.td, textAlign: "center", fontWeight: 700, color: "#0093dd" }}>{row.footfall || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{row.male || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{row.female || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{row.other || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{row.age_u18 || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{row.age_1830 || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{row.age_3145 || "—"}</td>
                        <td style={{ ...styles.td, textAlign: "center", borderRight: "2px solid #ddd" }}>{(row.age_4660 + row.age_60plus) || "—"}</td>
                        {/* Exits */}
                        <td style={{ ...styles.td, textAlign: "center", fontWeight: 700, color: "#d94f2b" }}>{row.exits || "—"}</td>
                        {SERVICES.map((s, si) => (
                          <td key={s.key} style={{ ...styles.td, textAlign: "center", borderRight: si === SERVICES.length - 1 ? "2px solid #ddd" : undefined }}>{row[s.key] || "—"}</td>
                        ))}
                        {/* Consumables */}
                        {CONSUMABLE_FIELDS.map(f => (
                          <td key={f.key} style={{ ...styles.td, textAlign: "center", color: "#2d7a4f" }}>{row[f.key] || "—"}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
    display: "flex", alignItems: "flex-start", justifyContent: "center",
    zIndex: 1000, padding: 16, overflowY: "auto",
  },
  panel: {
    background: "#f5f5f3", borderRadius: 16,
    width: "100%", maxWidth: 1100,
    boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
    marginTop: 24, marginBottom: 24, overflow: "hidden",
  },
  header: {
    background: "#1a3a4a", color: "#fff",
    padding: "14px 24px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    flexWrap: "wrap", gap: 10,
  },
  dateLabel: { fontSize: 12, color: "rgba(255,255,255,0.75)" },
  datePicker: {
    padding: "5px 8px", borderRadius: 7, border: "none",
    fontSize: 13, background: "rgba(255,255,255,0.15)", color: "#fff", cursor: "pointer",
  },
  closeBtn: {
    background: "none", border: "none",
    fontSize: 20, cursor: "pointer", color: "#fff", opacity: 0.8,
  },
  body: { padding: 20 },
  rangeBtn: (active) => ({
    padding: "5px 12px", borderRadius: 20, fontSize: 12, cursor: "pointer",
    border: active ? "2px solid #1a3a4a" : "2px solid #e0e0e0",
    background: active ? "#d8eaf4" : "#fafafa",
    color: active ? "#1a3a4a" : "#555", fontWeight: active ? 700 : 400,
  }),
  toggleRow: {
    display: "flex", border: "1.5px solid #ddd", borderRadius: 8, overflow: "hidden",
  },
  toggleBtn: (active) => ({
    padding: "6px 14px", fontSize: 13, fontWeight: active ? 700 : 400,
    background: active ? "#1a3a4a" : "#fff",
    color: active ? "#fff" : "#555",
    border: "none", cursor: "pointer",
  }),
  downloadBtn: (disabled) => ({
    padding: "6px 16px", fontSize: 13, fontWeight: 700,
    background: disabled ? "#ccc" : "#28a745",
    color: "#fff", border: "none", borderRadius: 8,
    cursor: disabled ? "not-allowed" : "pointer",
  }),
  empty: { textAlign: "center", color: "#888", padding: "40px 0", fontSize: 14 },
  error: { textAlign: "center", color: "#c0392b", background: "#fdf0ef", border: "1px solid #f5c6c2", borderRadius: 8, padding: "16px 24px", fontSize: 14 },
  table: {
    width: "100%", borderCollapse: "collapse", fontSize: 13,
    background: "#fff", borderRadius: 10, overflow: "hidden",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  th: {
    padding: "10px 12px", background: "#f0f0ee",
    fontWeight: 700, fontSize: 12, color: "#444",
    textAlign: "left", borderBottom: "2px solid #ddd",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "9px 12px", borderBottom: "1px solid #f0f0f0",
    fontSize: 13, color: "#1a1a1a",
  },
};
